import { useHistory } from "react-router-dom";

import {
  defaultErrorMessage,
  defaultRetryContactMessage,
  isNotFoundError,
} from "lib/axios/constants";
import * as routes from "routes";
import { useListings } from "services/listings";
import { Asset } from "services/listings/types";
import { ListingValuation, ListingWithIncludes } from "services/listings/types";
import { useProxies } from "services/proxies";
import { Proxy } from "services/proxies/types";
import { ValuationDescription } from "services/valuationDescriptions/types";

export const useAssetDetailData = (
  listingId: number,
): {
  asset: Asset | undefined;
  listing: ListingWithIncludes | undefined;
  valuation: ListingValuation | undefined;
  valuationDescription: ValuationDescription | null;
  proxy: Proxy | undefined;
  isLoading: boolean;
  isError: boolean;
  handleRefreshListing: () => Promise<void>;
  handleRefreshProxy: () => Promise<void>;
} => {
  const { listingQueries } = useListings(listingId);
  const history = useHistory();

  if (listingQueries?.isError) {
    const errorMessage = listingQueries?.error?.message || "";
    if (isNotFoundError(errorMessage)) {
      history.push(routes.orderBookOpenListingsNoParams(), {
        title: "Asset Not Available",
        content: ["The asset you are looking for is no longer available."],
      });
    } else {
      history.push(routes.orderBookOpenListingsNoParams(), {
        title: defaultErrorMessage,
        content: [defaultRetryContactMessage],
      });
    }
  }

  const listing = listingQueries.data;
  const valuation = listingQueries.data?.valuation;
  const valuationDescription =
    listingQueries.data?.valuation_description || null;
  const asset = listingQueries.data?.asset;

  const proxiesQuery = useProxies(listing);
  const proxy = proxiesQuery.data?.[0];

  const handleRefreshListing = async () => {
    await listingQueries.refetch();
    if (!listing?.is_owner) {
      await proxiesQuery.refetch();
    }
  };

  const handleRefreshProxy = async () => {
    if (!listing?.is_owner) {
      await proxiesQuery.refetch();
    }
  };

  const isLoading =
    listingQueries.isLoading ||
    (proxiesQuery.isLoading && !proxiesQuery.isIdle);

  const isError = listingQueries.isError || proxiesQuery.isError;

  return {
    asset,
    listing,
    valuation,
    valuationDescription,
    proxy,
    isLoading,
    isError,
    handleRefreshListing,
    handleRefreshProxy,
  };
};
